/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, useRef, useState } from "react";
import { Else, If, Then } from "react-if";
import { useParams } from "react-router-dom";
import { Spacer, theme } from "@nordcloud/gnui";
import { ErrorWrap, ListLoader, LoaderWrap } from "~/components";
import {
  CommonListProps,
  ESTATE_COLUMNS,
  mapEstateRecord,
  ResourcesList,
} from "~/components/EstateRecordsList";
import { ERROR_COLORS } from "~/constants";
import {
  estateV2FilterFields,
  useCurrency,
  useEstateRecords,
  useEstateSort,
  useFilteredQueryState,
  useScrollAfterPaging,
} from "~/hooks";
import { useCustomers } from "~/services/customers";
import { showError } from "~/services/toast";
import { isNotEmpty, isNotNil, noop } from "~/tools";
import { convertTags } from "~/views/estate/helpers";
import { CloudProviderType, Provider } from "../../types";
import { isKubernetesOrOpenshift } from "../../utils";
import {
  EstateRecordsFilter,
  EstateRecordsNotification,
  ResourcesListKubernetes,
} from "./components";
import { EstateQueryState } from "./types";

type RouteParams = {
  accountId: string;
};

type Props = {
  providerId: string;
  providerType?: CloudProviderType;
  provider: Provider;
};

export function EstateRecords({ providerId, provider, providerType }: Props) {
  const { isAdminLite } = useCustomers();
  const scrollToRef = useRef<HTMLDivElement>(null);
  const { accountId = "" } = useParams<RouteParams>();
  const { currency } = useCurrency();
  const { state, updateQueryState } =
    useFilteredQueryState(estateV2FilterFields);

  const areMetricsEnabled = isNotNil(providerType)
    ? isKubernetesOrOpenshift(providerType)
    : false;

  const resolvedAccountId =
    isNotNil(providerType) && isKubernetesOrOpenshift(providerType)
      ? accountId
      : providerId;

  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const {
    page,
    limit,
    query,
    field,
    order,
    tags,
    metadata,
    ...rawFilters
  }: EstateQueryState = state;

  const [searchInput, setSearchInput] = useState(query);
  const { sortField, sortDirection, onSortSelect, onSortOrderChange } =
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    useEstateSort({ field, order, updateQueryState });

  const filters = useMemo(() => {
    return {
      ...rawFilters,
      tags: convertTags(tags),
      metadata: convertTags(metadata),
    };
  }, [rawFilters, tags, metadata]);

  const { data, loading, error } = useEstateRecords(
    {
      variables: {
        page,
        limit,
        sort: {
          field: sortField,
          order: sortDirection,
        },
        filter: {
          ...filters,
          account: [resolvedAccountId],
        },
        query,
      },
    },
    showError
  );

  const {
    data: metrics,
    loading: areMetricsLoading,
    error: loadingError,
  } = useEstateRecords(
    {
      variables: {
        limit,
        page,
        sort: {
          field: sortField,
          order: sortDirection,
        },
        filter: {
          ...filters,
          account: [providerId],
        },
        query,
      },
      skip: !areMetricsEnabled,
    },
    noop
  );

  const rows = useMemo(
    () =>
      data?.resources?.map((record) => mapEstateRecord(record, currency)) ?? [],
    [data, currency]
  );

  const columns = useMemo(() => {
    const excludedColumns = isAdminLite ? ["application"] : [];

    return ESTATE_COLUMNS.filter(
      (column) => !excludedColumns.includes(column.accessor)
    );
  }, [isAdminLite]);

  const hasResources = isNotEmpty(rows);
  const hasMetricsResources = isNotEmpty(metrics?.resources ?? []);
  const estateCount = data?.count ?? 0;
  const showPagination = estateCount > 0;

  useScrollAfterPaging({
    scrollToRef,
    enabled: !loading || !areMetricsLoading,
  });

  const commonListProps: CommonListProps = {
    hasResources,
    data,
    columns,
    rows,
    showPagination,
    estateCount,
  };

  return (
    <div ref={scrollToRef}>
      <ErrorWrap
        error={error || loadingError}
        status={
          hasResources || hasMetricsResources
            ? ERROR_COLORS.notification
            : ERROR_COLORS.danger
        }
      >
        <EstateRecordsNotification
          provider={provider}
          providerType={providerType}
        />
        <Spacer height={theme.spacing.spacing04} />
        <EstateRecordsFilter
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          filters={filters}
          sortField={sortField}
          sortDirection={sortDirection}
          query={query}
          onSortSelect={onSortSelect}
          onSortOrderChange={onSortOrderChange}
        />
        <LoaderWrap
          isBackground
          loading={loading || areMetricsLoading}
          Component={ListLoader}
        >
          <If condition={areMetricsEnabled}>
            <Then>
              <ResourcesListKubernetes
                hasMetricsResources={hasMetricsResources}
                metrics={metrics}
                areMetricsEnabled={areMetricsEnabled}
                metadata={commonListProps}
              />
            </Then>
            <Else>
              <ResourcesList metadata={commonListProps} />
            </Else>
          </If>
        </LoaderWrap>
      </ErrorWrap>
    </div>
  );
}
